import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLink,
    faCloud,
    faCalendarAlt,
    faMagic,
    faEnvelope,
    faCirclePlay,
    faBars,
    faEyeSlash,
    faEllipsisVertical,
    faEye,
    faHandHoldingDollar,
    faImage,
    faHeart,
} from '@fortawesome/free-solid-svg-icons';
import {
    EditOutlined,
    UpOutlined,
    DownOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { MovableBlock, ActionButton, StyledMenu } from './styled';
import { faSpotify, faApple } from '@fortawesome/free-brands-svg-icons';

const ItemTypes = {
    BLOCK: 'block',
};

const getPopoverContent = (
    type,
    productType,
    onEdit,
    onDelete,
    onTogglePublish,
    onMoveUp,
    onMoveDown,
    isPublished,
) => {
    return (
        <StyledMenu>
            {/* Only show Edit if NOT Spotify */}
            {(
                type === 'headline' ||
                (
                    type === 'product' &&
                    productType !== 'spotify' &&
                    productType !== 'apple' &&
                    productType !== 'image'
                )
            ) && (
                    <StyledMenu.Item key="edit" onClick={onEdit}>
                        <EditOutlined /> Edit
                    </StyledMenu.Item>
                )}
            {type === 'product' && (
                <StyledMenu.Item key="togglePublish" onClick={onTogglePublish}>
                    {isPublished ? (
                        <>
                            <FontAwesomeIcon icon={faEyeSlash} className="fa-icon" /> Unpublish
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faEye} className="fa-icon" /> Publish
                        </>
                    )}
                </StyledMenu.Item>
            )}
            <StyledMenu.Item key="moveUp" onClick={onMoveUp}>
                <UpOutlined /> Move Up
            </StyledMenu.Item>
            <StyledMenu.Item key="moveDown" onClick={onMoveDown}>
                <DownOutlined /> Move Down
            </StyledMenu.Item>
            <StyledMenu.Item key="delete" onClick={onDelete}>
                <DeleteOutlined /> Delete
            </StyledMenu.Item>
        </StyledMenu>
    );
};

const MovableItem = ({
    id,
    text,
    onDelete,
    type,
    onEdit,
    onTogglePublish,
    thumbnail,
    index,
    moveBlock,
    productType,
    price,
    isPublished,
    cardType,
    creatorImages = [],
    creatorImagesV2 = [],
    animationDelay,
}) => {
    const [{ isDragging }, ref] = useDrag({
        type: ItemTypes.BLOCK,
        item: { id, index, type },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            const didDrop = monitor.didDrop();
            if (didDrop) {
                moveBlock(item.index, item.index);
            }
        },
    });

    const [, drop] = useDrop({
        accept: ItemTypes.BLOCK,
        hover: (draggedItem) => {
            if (draggedItem.index !== index) {
                moveBlock(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    const productTypeIcons = {
        url: <FontAwesomeIcon icon={faLink} style={{ color: '#3B82F6' }} />,
        digital: <FontAwesomeIcon icon={faCloud} style={{ color: '#10B981' }} />,
        custom: <FontAwesomeIcon icon={faMagic} style={{ color: '#F97316' }} />,
        email: <FontAwesomeIcon icon={faEnvelope} style={{ color: '#6366F1' }} />,
        video: <FontAwesomeIcon icon={faCirclePlay} style={{ color: '#F59E0B' }} />,
        booking: <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#000' }} />,
        spotify: <FontAwesomeIcon icon={faSpotify} style={{ color: '#1DB954' }} />,
        apple: <FontAwesomeIcon icon={faApple} style={{ color: '#374151' }} />,
        tip: <FontAwesomeIcon icon={faHandHoldingDollar} style={{ color: '#14B8A6' }} />,
        image: <FontAwesomeIcon icon={faImage} style={{ color: '#6B7280' }} />,
        engagement: <FontAwesomeIcon icon={faHeart} style={{ color: '#EF4444' }} />,
    };

    const handleMoveUp = () => {
        if (index > 0) {
            moveBlock(index, index - 1);
        }
    };

    const handleMoveDown = () => {
        moveBlock(index, index + 1);
    };

    const handleEdit = () => {
        onEdit(id, type, text, productType);
    };

    const opacity = isDragging ? 0.4 : 1;

    // 1) Combine old or new images into a single array of *just URLs* to display
    let imagesToShow = [];
    if (creatorImagesV2.length > 0) {
        // For new products, each item is an object { url, shortTitle, link }
        imagesToShow = creatorImagesV2.map((imgObj) => imgObj.url);
    } else if (creatorImages.length > 0) {
        // For older products, it might just be an array of strings
        imagesToShow = creatorImages;
    }

    return (
        <MovableBlock
            ref={(node) => ref(drop(node))}
            style={{ opacity }}
            isPublished={isPublished}
            cardType={cardType}
            animationDelay={animationDelay}
        >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <FontAwesomeIcon icon={faBars} className="drag-handle" />

                {type === 'product' && productType === 'image' && imagesToShow.length > 0 ? (
                    (() => {
                        const maxToShow = 2;
                        const displayed = imagesToShow.slice(0, maxToShow);
                        const remainingCount = imagesToShow.length - maxToShow;

                        return (
                            <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                                {displayed.map((imgUrl, idx) => (
                                    <img
                                        key={idx}
                                        src={imgUrl}
                                        alt={`product-${idx}`}
                                        className="block-image"
                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                    />
                                ))}

                                {/* If there's more than `maxToShow`, show something like "+X more" */}
                                {remainingCount > 0 && (
                                    <span style={{ fontSize: '14px', marginRight: '5px' }}>
                                        +{remainingCount} images
                                    </span>
                                )}
                            </div>
                        );
                    })()
                ) : type === 'product' && thumbnail && (
                    <img
                        src={thumbnail}
                        alt="Product"
                        className="block-image"
                    />
                )}

                <div className="block-content">
                    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div className="block-header">
                            <span className="block-title">{text}</span>

                            {/* This wrapper ensures the icons get pushed to the right */}

                        </div>
                    </div>
                    {type === 'product' && price !== null && (
                        <span className="block-price">
                            {price === 0 ? 'Free' : `$${Number(price).toFixed(2)}`}
                        </span>
                    )}
                </div>

                <Popover
                    content={getPopoverContent(
                        type,
                        productType,
                        handleEdit,
                        () => onDelete(id, type, productType),
                        () => onTogglePublish(id),
                        handleMoveUp,
                        handleMoveDown,
                        isPublished
                    )}
                    trigger="click"
                    placement="bottomRight"
                    arrow={false}
                    overlayClassName="block-popover"
                >
                    {/* Single flex container: icons + ellipsis */}
                    <div
                        style={{
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <div style={{ marginRight: '5px' }}>
                            {/* Icons */}
                            {type === 'product' && !isPublished && (
                                <span className="block-visibility">
                                    <FontAwesomeIcon icon={faEyeSlash} />
                                </span>
                            )}
                            {type === 'product' && productType && (
                                <span className="block-type-icon">
                                    {productTypeIcons[productType.toLowerCase()]}
                                </span>
                            )}
                        </div>

                        {/* Ellipsis button */}
                        <ActionButton icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
                    </div>
                </Popover>
            </div>
        </MovableBlock>
    );
};

export default MovableItem;